import { Bell, CheckCircle, AlertTriangle, Info, Clock } from 'lucide-react';
import { Dropdown, DropdownHeader, DropdownItem, DropdownFooter } from '@/components/ui';
import { Link } from '@tanstack/react-router';

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'success' | 'task';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

// TODO: Replace with actual API data when Aelita insights are implemented
const mockNotifications: Notification[] = [];

export function NotificationsDropdown() {
  const unreadCount = mockNotifications.filter((n) => !n.read).length;

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'task':
        return <Clock className="h-5 w-5 text-primary-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };

  return (
    <Dropdown
      trigger={
        <button className="relative p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
          )}
        </button>
      }
      align="right"
      width="w-96"
    >
      <DropdownHeader>
        <div className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <span className="text-xs bg-primary-100 dark:bg-primary-950 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
      </DropdownHeader>

      <div className="max-h-96 overflow-y-auto">
        {mockNotifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell className="h-12 w-12 text-gray-300 dark:text-gray-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 font-medium">No notifications yet</p>
            <p className="text-gray-400 text-sm mt-1">
              You&apos;ll see updates about your projects and tasks here
            </p>
          </div>
        ) : (
          mockNotifications.map((notification) => (
            <DropdownItem key={notification.id} className={notification.read ? 'opacity-60' : ''}>
              <div className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">{getIcon(notification.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{notification.title}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{notification.message}</p>
                  <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                </div>
              </div>
            </DropdownItem>
          ))
        )}
      </div>

      <DropdownFooter>
        <Link
          to="/settings"
          className="block text-center text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
        >
          Notification Settings
        </Link>
      </DropdownFooter>
    </Dropdown>
  );
}
